import type { LlmEntry } from "../config";

export function friendlyLabel(id: string, meta?: Map<string, LlmEntry>): string {
  const entry = meta?.get(id);
  if (entry?.label) return entry.label;
  const slash = id.indexOf("/");
  return slash > 0 ? id.slice(slash + 1) : id;
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Walks the text nodes of `root` and replaces every exact occurrence of any
 * string in `ids` with a span.llm-pill whose text is `labelFor(id)` and whose
 * title is the raw id. Idempotent: once IDs are replaced with spans, subsequent
 * passes no longer match text nodes containing the friendly label.
 */
export function pillifyInPlace(
  root: HTMLElement,
  ids: string[],
  labelFor: (id: string) => string,
): void {
  if (ids.length === 0) return;
  const sorted = [...ids].sort((a, b) => b.length - a.length);
  const pattern = new RegExp(sorted.map(escapeRegex).join("|"), "g");
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const texts: Text[] = [];
  let n: Node | null;
  while ((n = walker.nextNode())) texts.push(n as Text);
  for (const t of texts) {
    const s = t.nodeValue ?? "";
    pattern.lastIndex = 0;
    if (!pattern.test(s)) continue;
    pattern.lastIndex = 0;
    const frag = document.createDocumentFragment();
    let i = 0;
    let m: RegExpExecArray | null;
    while ((m = pattern.exec(s)) !== null) {
      if (m.index > i) frag.appendChild(document.createTextNode(s.slice(i, m.index)));
      const id = m[0];
      const span = document.createElement("span");
      span.className = "llm-pill";
      span.title = id;
      span.textContent = labelFor(id);
      frag.appendChild(span);
      i = m.index + id.length;
    }
    if (i < s.length) frag.appendChild(document.createTextNode(s.slice(i)));
    t.parentNode?.replaceChild(frag, t);
  }
}

/**
 * String-level equivalent of pillifyInPlace — replaces every exact occurrence
 * of any id in `ids` with `labelFor(id)`. Used by text/markdown exports so the
 * rapporteur summary reads the same as it does on screen.
 */
export function replaceIdsInText(
  s: string,
  ids: string[],
  labelFor: (id: string) => string,
): string {
  if (ids.length === 0 || !s) return s;
  const sorted = [...ids].sort((a, b) => b.length - a.length);
  const pattern = new RegExp(sorted.map(escapeRegex).join("|"), "g");
  return s.replace(pattern, (m) => labelFor(m));
}
